import { StyleProvider } from '@ant-design/cssinjs';
import { ConfigProvider } from 'antd';
import React, { useState } from 'react';
import { createRoot } from 'react-dom/client';
import { IntlProvider } from 'react-intl';

import { RightProvider } from '@/components/RightProvider';
import en from '@/lang/en.json';
import zh from '@/lang/zh.json';
import theme from '@/theme';

import VideoPlayer from '.';

interface VideoPlayerShowOptions {
  src: string;
  title?: string;
  onClose?: () => void;
}

const ProxyVideoPlayer: React.FC<{
  options: VideoPlayerShowOptions;
  destroyHandle: () => void;
}> = ({ options, destroyHandle }) => {
  const [visible, setVisible] = useState(true);

  const handleClose = () => {
    setVisible(false);
    // Call user's onClose callback
    options.onClose?.();
    // Wait for animation to complete before destroying
    setTimeout(() => {
      destroyHandle();
    }, 300);
  };

  if (!visible) return null;

  return <VideoPlayer src={options.src} title={options.title} onClose={handleClose} />;
};

export const VideoPlayerImperative = {
  show: (options: VideoPlayerShowOptions) => {
    const container = document.createElement('div');
    document.body.appendChild(container);
    const root = createRoot(container);
    let isDestroyed = false;

    const destroyHandle = () => {
      if (isDestroyed) return;
      isDestroyed = true;

      try {
        root.unmount();
      } catch (error) {
        console.warn('Failed to unmount root:', error);
      }

      try {
        if (container.parentNode === document.body) {
          document.body.removeChild(container);
        }
      } catch (error) {
        console.warn('Failed to remove container:', error);
      }
    };

    const lang = navigator.language.startsWith('zh') ? 'zh' : 'en';

    root.render(
      <RightProvider>
        <IntlProvider locale={lang} messages={lang === 'en' ? en : zh}>
          <ConfigProvider theme={theme}>
            <StyleProvider hashPriority="high">
              <ProxyVideoPlayer options={options} destroyHandle={destroyHandle} />
            </StyleProvider>
          </ConfigProvider>
        </IntlProvider>
      </RightProvider>,
    );

    return destroyHandle;
  },
};
